import { ArrowRight } from 'lucide-react'
import { Link, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api, ApiError } from '@/lib/api'
import type { OrderLine, PurchaseRequestDetail } from '@/lib/api-types'
import { formatDate, formatMoney, formatQty } from '@/lib/format'
import { PURCHASE_REQUEST_STATUS_LABELS, purchaseRequestChipClasses } from '@/lib/status'
import { Button } from '@/components/ui/button'
import { DataTable, type DataTableColumn } from '@/components/ui/data-table'
import { DescriptionList, type DescriptionItem } from '@/components/ui/description-list'
import { PageHeader } from '@/components/ui/page-header'
import { SectionHeader } from '@/components/ui/section-header'
import { Spinner } from '@/components/ui/spinner'
import { StatusChip } from '@/components/ui/status-chip'

export function PurchasingDetailPage() {
  const { id = '' } = useParams()

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['purchase-request', id],
    queryFn: ({ signal }) => api.getPurchaseRequest(id, { signal }),
    staleTime: 15_000,
  })

  if (isPending) {
    return (
      <div className="flex justify-center py-24">
        <Spinner label="Loading this order request…" />
      </div>
    )
  }

  if (error) {
    const missing = error instanceof ApiError && error.status === 404
    return (
      <div className="py-24 text-center">
        <p className="micro-label">{missing ? 'Not found' : 'Something went wrong'}</p>
        <p className="mt-3 text-[0.9375rem] text-inkMid">
          {missing
            ? 'This order request doesn’t exist — or it belongs to another account.'
            : 'We couldn’t load this order request.'}
        </p>
        <div className="mt-6 flex justify-center gap-3">
          {!missing ? (
            <Button variant="neutral" size="sm" onClick={() => refetch()}>
              Try again
            </Button>
          ) : null}
          <Button asChild variant="quiet" size="sm">
            <Link to="/purchasing">Back to Purchasing</Link>
          </Button>
        </div>
      </div>
    )
  }

  return <PurchasingDetailView detail={data} />
}

function PurchasingDetailView({ detail }: { detail: PurchaseRequestDetail }) {
  const facts: DescriptionItem[] = []
  if (detail.poNumber) facts.push({ label: 'PO number', value: detail.poNumber, mono: true })
  facts.push({
    label: 'Estimated total',
    value: formatMoney(detail.amount, detail.currency),
    mono: true,
  })
  if (detail.submittedAt) {
    facts.push({
      label: 'Sent',
      value: <time dateTime={detail.submittedAt}>{formatDate(detail.submittedAt)}</time>,
      mono: true,
    })
  }
  if (detail.submittedByName) {
    facts.push({ label: 'Submitted by', value: detail.submittedByName })
  }

  return (
    <article className="mx-auto max-w-data">
      <nav aria-label="Breadcrumb" className="flex items-baseline gap-2 font-mono text-[0.8125rem]">
        <Link
          to="/purchasing"
          className="rounded-sm text-mute underline-offset-4 transition-colors duration-[180ms] ease-editorial hover:text-brand hover:underline"
        >
          Purchasing
        </Link>
        <span aria-hidden="true" className="text-muteSoft">
          ·
        </span>
        <span aria-current="page" className="text-inkMid">
          {detail.name}
        </span>
      </nav>

      <PageHeader
        className="mt-3"
        title={detail.name}
        meta={
          <StatusChip
            label={
              detail.status === 'accepted' && detail.stageLabel
                ? `Accepted · ${detail.stageLabel}`
                : PURCHASE_REQUEST_STATUS_LABELS[detail.status]
            }
            chipClasses={purchaseRequestChipClasses(detail.status)}
          />
        }
      />

      <DescriptionList className="mt-5 border-y border-rule/60 py-4" columns={4} items={facts} />

      {detail.status === 'submitted' ? (
        <p
          role="status"
          className="mt-6 rounded-md border border-amber/40 bg-amber/10 px-4 py-3.5 text-sm leading-relaxed text-inkMid"
        >
          <span className="font-semibold text-ink">With Green Line for review.</span> Once your
          team accepts it, this order request becomes an order and moves to the Orders page.
        </p>
      ) : null}

      {detail.status === 'declined' ? (
        <div
          role="status"
          className="mt-6 rounded-md border border-rust/30 bg-rust/5 px-4 py-3.5"
        >
          <p className="text-sm font-semibold text-rust">This order request was declined.</p>
          {detail.reviewNote ? (
            <p className="mt-1 text-sm leading-relaxed text-inkMid">{detail.reviewNote}</p>
          ) : (
            <p className="mt-1 text-sm leading-relaxed text-inkMid">
              Reach out to your Green Line contact for the details.
            </p>
          )}
        </div>
      ) : null}

      {detail.status === 'accepted' && detail.orderId ? (
        <Link
          to={`/orders/${detail.orderId}`}
          className="mt-6 inline-flex items-center gap-1 rounded-sm text-sm font-semibold text-brand underline-offset-4 hover:underline"
        >
          This request became an order — follow it there
          <ArrowRight aria-hidden="true" className="size-3.5" />
        </Link>
      ) : null}

      <section aria-labelledby="request-lines-heading" className="mt-10">
        <SectionHeader id="request-lines-heading" title="Lines" />
        <div className="mt-4">
          <DataTable
            columns={lineColumns(detail.currency)}
            rows={detail.lines}
            rowKey={(line) => line.id}
            caption="Requested lines"
          />
        </div>
      </section>
    </article>
  )
}

function lineColumns(currency: string): DataTableColumn<OrderLine>[] {
  return [
    {
      id: 'product',
      header: 'Product',
      flex: true,
      widthClassName: 'min-w-48',
      cell: (line) => (
        <span className="flex flex-col">
          <span className="truncate text-[0.9375rem] font-medium text-ink" title={line.productName}>
            {line.productName}
          </span>
          <span className="mt-0.5 truncate font-mono text-xs text-mute">
            {line.itemCode ?? ''}
            {line.packSizeKg
              ? `${line.itemCode ? ' · ' : ''}${formatQty(line.packSizeKg)} kg pack`
              : ''}
          </span>
        </span>
      ),
    },
    {
      id: 'quantity',
      header: 'Quantity',
      align: 'right',
      widthClassName: 'w-28',
      nowrap: true,
      cell: (line) => (
        <span>
          {formatQty(line.quantity)}
          {line.uom ? <span className="text-mute"> {line.uom}</span> : null}
        </span>
      ),
    },
    {
      id: 'pricePerKg',
      header: 'Price / kg',
      align: 'right',
      widthClassName: 'w-28',
      nowrap: true,
      cell: (line) => (line.pricePerKg != null ? formatMoney(line.pricePerKg, currency) : '—'),
    },
    {
      id: 'total',
      header: 'Total',
      align: 'right',
      widthClassName: 'w-28',
      nowrap: true,
      cell: (line) => formatMoney(line.lineTotal, currency),
    },
  ]
}
